<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import { useAlert } from 'dashboard/composables';
import { useLeadSubmissionsStore } from 'dashboard/stores/leadSubmissions';
import { usePipelinesStore } from 'dashboard/stores/pipelines';
import { useLeadIntentsStore } from 'dashboard/stores/leadIntents';

const emit = defineEmits(['converted']);
const { t } = useI18n();
const submissionsStore = useLeadSubmissionsStore();
const pipelinesStore = usePipelinesStore();
const intentsStore = useLeadIntentsStore();

const dialogRef = ref(null);
const submission = ref(null);
const isLoading = ref(false);

const overrides = ref({
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  intent_id: '',
  pipeline_id: '',
  stage_id: '',
  classification: '',
});

const selectedPipeline = computed(() =>
  pipelinesStore.list.find(p => p.id === Number(overrides.value.pipeline_id))
);
const stages = computed(() => selectedPipeline.value?.stages || []);
const intents = computed(() => intentsStore.list);

const classificationOptions = computed(() => [
  { value: '', label: '-' },
  { value: 'hot', label: 'Quente' },
  { value: 'warm', label: 'Morno' },
  { value: 'cold', label: 'Frio' },
]);

const pipelineOptions = computed(() => [
  { value: '', label: 'Selecione...' },
  ...pipelinesStore.list.map(p => ({ value: p.id, label: p.name })),
]);

const stageOptions = computed(() => [
  { value: '', label: 'Primeira etapa' },
  ...stages.value.map(s => ({ value: s.id, label: s.name })),
]);

const intentOptions = computed(() => [
  { value: '', label: '-' },
  ...intents.value.map(i => ({ value: i.id, label: i.name })),
]);

function open(sub) {
  submission.value = sub;
  overrides.value = {
    contact_name: sub?.contact_name || '',
    contact_email: sub?.contact_email || '',
    contact_phone: sub?.contact_phone || '',
    intent_id: '',
    pipeline_id: '',
    stage_id: '',
    classification: sub?.classification || '',
  };
  pipelinesStore.fetchAll();
  intentsStore.fetch();
  dialogRef.value?.open();
}

defineExpose({ open });

async function handleConvert() {
  if (!overrides.value.pipeline_id) {
    useAlert('Selecione um pipeline.');
    return;
  }
  isLoading.value = true;
  try {
    const result = await submissionsStore.convert(submission.value.id, overrides.value);
    useAlert('Lead convertido com sucesso.');
    emit('converted', result);
    dialogRef.value?.close();
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Erro ao converter.');
  } finally {
    isLoading.value = false;
  }
}
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('LEADS.INBOX.ACTIONS.CONVERT')"
    :confirm-button-label="t('LEADS.INBOX.ACTIONS.CONVERT')"
    :is-loading="isLoading"
    :disable-confirm-button="isLoading || !overrides.pipeline_id"
    @confirm="handleConvert"
  >
    <template #description>
      <p class="text-sm text-n-slate-11 mb-4">
        A classificação IA foi pré-preenchida - edite antes de confirmar se necessário.
      </p>
    </template>

    <div class="flex flex-col gap-4">
      <!-- Contact -->
      <div class="grid grid-cols-2 gap-3">
        <div class="flex flex-col gap-1.5">
          <label class="text-body-small font-medium text-n-slate-12">Nome</label>
          <input
            v-model="overrides.contact_name"
            type="text"
            class="px-3 py-2 text-body-small rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:border-n-brand"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-body-small font-medium text-n-slate-12">E-mail</label>
          <input
            v-model="overrides.contact_email"
            type="email"
            class="px-3 py-2 text-body-small rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:border-n-brand"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-body-small font-medium text-n-slate-12">Telefone</label>
          <input
            v-model="overrides.contact_phone"
            type="text"
            class="px-3 py-2 text-body-small rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:border-n-brand"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-body-small font-medium text-n-slate-12">Classificação</label>
          <Select
            v-model="overrides.classification"
            :options="classificationOptions"
          />
        </div>
      </div>

      <!-- Pipeline + Stage -->
      <div class="grid grid-cols-2 gap-3">
        <div class="flex flex-col gap-1.5">
          <label class="text-body-small font-medium text-n-slate-12">Pipeline *</label>
          <Select v-model="overrides.pipeline_id" :options="pipelineOptions" />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-body-small font-medium text-n-slate-12">Etapa inicial</label>
          <Select
            v-model="overrides.stage_id"
            :options="stageOptions"
            :disabled="!overrides.pipeline_id"
          />
        </div>
      </div>

      <!-- Intenção -->
      <div class="flex flex-col gap-1.5">
        <label class="text-body-small font-medium text-n-slate-12">Intenção</label>
        <Select v-model="overrides.intent_id" :options="intentOptions" />
      </div>
    </div>
  </Dialog>
</template>